import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { UserRole } from '../models/user.model';
import { RolePermissions, getPermissionsForRole } from '../config/permissions.config';

/**
 * PermissionService — single access point for all permission checks.
 *
 * Usage in templates:
 *   *ngIf="permSvc.can('clearance.approveRelease')"
 *   *ngIf="permSvc.hasRole('cs_manager')"
 *   *ngIf="permSvc.hasAnyRole(['cs_manager','cs_agent_senior'])"
 *
 * Usage in code:
 *   if (this.permSvc.can('finance.viewCostCenters')) { ... }
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {

  constructor(private auth: AuthService) {}

  /** Get the current user's full permissions object */
  getPermissions(): RolePermissions | null {
    const user = this.auth.getUser();
    if (!user) return null;
    return getPermissionsForRole(user.role as UserRole);
  }

  /**
   * Check a dot-notation permission key.
   * Examples:
   *   can('clearance.approveRelease')
   *   can('finance.viewCostCenters')
   *   can('ui.simplified')
   */
  can(permission: string): boolean {
    const perms = this.getPermissions();
    if (!perms) return false;

    const parts = permission.split('.');
    if (parts.length !== 2) return false;

    const [section, key] = parts;
    const sectionPerms = (perms as any)[section];
    if (!sectionPerms) return false;

    const value = sectionPerms[key];
    if (typeof value === 'boolean') return value;
    if (Array.isArray(value)) return value.length > 0;
    return false;
  }

  /**
   * Check if user can act on a specific clearance step number.
   * Usage: canActOnStep(7)
   */
  canActOnStep(step: number): boolean {
    const perms = this.getPermissions();
    if (!perms) return false;
    return perms.clearance.editSteps.includes(step);
  }

  /** Check if user has a specific role */
  hasRole(role: UserRole): boolean {
    return this.auth.getUser()?.role === role;
  }

  /** Check if user has any of the given roles */
  hasAnyRole(roles: UserRole[]): boolean {
    const userRole = this.auth.getUser()?.role;
    if (!userRole) return false;
    return roles.includes(userRole as UserRole);
  }

  /** Check if user is owner or admin */
  isOwnerOrAdmin(): boolean {
    return this.hasAnyRole(['owner', 'admin']);
  }

  /** Check if user has simplified UI (customs_broker_external) */
  isSimplifiedUI(): boolean {
    return this.can('ui.simplified');
  }

  /** Returns the current user's role */
  getRole(): UserRole | null {
    return (this.auth.getUser()?.role as UserRole) ?? null;
  }
}
